/**
 * StudyTile — one tile per study on the Studies dashboard: enrollment-status
 * badge, activity under the current lens, per-status stacked bar, top
 * coordinators, and the "Enrolling, no activity" opportunity flag.
 */

import { AlertTriangle, Link2 } from 'lucide-react';
import { StudyInfo, TimeLens, formatMinutes } from '../types';
import { StudyRollup, lensTotal, wholeVisitMinutes } from '../hooks/useInsightsData';
import { StatusBreakdownBar } from './StatusBreakdownBar';
import { EnrollmentStatusPill } from '../../ui';

interface StudyTileProps {
  study: StudyInfo | null; // null = unmapped raw study-name bucket
  rollup: StudyRollup | null;
  lens: TimeLens;
  /** Max lens-minutes across all tiles (for relative bar scaling). */
  maxMinutes: number;
  /** coordinatorKey -> display name for the top-coordinators line. */
  coordinatorNames: Map<string, string>;
  rank: number | null;
  /** False when the effort/visits fetch for this range failed: the minutes
   *  are UNKNOWN, not zero, so the idle flags must not claim otherwise. */
  activityKnown?: boolean;
}

export function StudyTile({ study, rollup, lens, maxMinutes, coordinatorNames, rank, activityKnown = true }: StudyTileProps) {
  const minutesByStatus = rollup?.minutesByStatus || {};
  const total = lensTotal(minutesByStatus, lens);
  // The idle flags say "no VISIT TIME in this range", so they read the
  // lens-independent whole-visit total (`wholeVisitMinutes`, the same sum
  // StatusBreakdownBar shows) — not `total`. Under the default `with_crc`
  // lens a study whose visits only logged lobby/investigator minutes has a
  // zero lens total, and flagging it idle directly above its own non-zero
  // visit counter is exactly the contradiction this avoids.
  const visitMinutes = wholeVisitMinutes(minutesByStatus);
  const enrollingIdle = activityKnown && study?.enrollment_status === 'enrolling' && visitMinutes <= 0;
  const startupIdle = activityKnown && study?.enrollment_status === 'startup' && visitMinutes <= 0;

  const topCoordinators = rollup
    ? [...rollup.coordinatorStatusMinutes.entries()]
        .map(([key, statusMinutes]) => ({ key, minutes: lensTotal(statusMinutes, lens) }))
        .filter((c) => c.minutes > 0)
        .sort((a, b) => b.minutes - a.minutes)
        .slice(0, 3)
    : [];

  return (
    <div className="flex flex-col gap-2 rounded-xl border border-gray-200 bg-white p-4 transition-all duration-150 hover:border-gray-300 hover:shadow-md dark:border-gray-700 dark:bg-gray-800 dark:hover:border-gray-600">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">
            {study ? study.study_nickname || study.protocol_number : rollup?.raw_name || '(no study)'}
          </p>
          <p className="truncate text-xs text-gray-500 dark:text-gray-400">
            {study ? study.protocol_number : 'Not linked to the registry'}
            {study?.sponsor_name ? ` · ${study.sponsor_name}` : ''}
          </p>
        </div>
        {rank !== null && total > 0 && (
          <span className="flex-shrink-0 text-xs font-semibold tabular-nums text-gray-400 dark:text-gray-500">#{rank}</span>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        {study?.enrollment_status && <EnrollmentStatusPill status={study.enrollment_status} />}
        {!study && (
          <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
            <Link2 size={12} aria-hidden="true" />
            Unmapped
          </span>
        )}
      </div>

      <div className="flex items-baseline gap-2">
        <span className="text-lg font-bold tabular-nums text-gray-900 dark:text-gray-100">{formatMinutes(total)}</span>
        <span
          className="text-xs text-gray-500 dark:text-gray-400"
          title="completed / scheduled (scheduled includes cancellations and no-shows)"
        >
          {rollup?.completed_count || 0}/{rollup?.visit_count || 0} visit{(rollup?.visit_count || 0) === 1 ? '' : 's'}
        </span>
      </div>

      <StatusBreakdownBar
        minutesByStatus={minutesByStatus}
        scale={maxMinutes > 0 ? total / maxMinutes : 0}
        showLegend={lens === 'breakdown'}
      />

      {topCoordinators.length > 0 && (
        <p className="truncate text-xs text-gray-500 dark:text-gray-400">
          {topCoordinators
            .map((c) => `${coordinatorNames.get(c.key) || 'Former or removed user'} ${formatMinutes(c.minutes)}`)
            .join(' · ')}
        </p>
      )}

      {enrollingIdle && (
        <p className="flex items-center gap-1.5 text-xs font-medium text-amber-700 dark:text-amber-400">
          <AlertTriangle size={14} aria-hidden="true" />
          Enrolling, but no visit time in this range
        </p>
      )}
      {startupIdle && (
        <p className="text-xs text-gray-400 dark:text-gray-500">In start-up — no visit activity expected yet</p>
      )}

      {rollup && (rollup.in_flight_count > 0 || rollup.incomplete_count > 0 || rollup.override_count > 0) && (
        <p className="text-[10px] uppercase tracking-widest text-gray-400 dark:text-gray-500">
          {[
            rollup.in_flight_count > 0 ? `${rollup.in_flight_count} in progress` : null,
            rollup.incomplete_count > 0 ? `${rollup.incomplete_count} never checked out` : null,
            rollup.override_count > 0 ? `${rollup.override_count} manual override${rollup.override_count === 1 ? '' : 's'}` : null,
          ]
            .filter(Boolean)
            .join(' · ')}
        </p>
      )}
    </div>
  );
}

export default StudyTile;
